/**
 * ProBookingLanding.jsx
 *
 * Landing page for the Online Booking section, reached from the hub.
 * Route: /pro/booking-settings  (ACCOUNT_OWNER)
 *
 * Two cards:
 *   - Booking Configuration  → /pro/booking-config
 *   - Stylist & Services     → /pro/booking-settings/stylists
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Button } from '@mui/material';
import TuneIcon        from '@mui/icons-material/Tune';
import PeopleAltIcon   from '@mui/icons-material/PeopleAlt';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';

const cards = [
  {
    icon:        <TuneIcon sx={{ fontSize: 36 }} />,
    colour:      '#7c3aed',
    title:       'Booking Configuration',
    description: 'Set your salon hours, booking page URL, deposit rules, and connect Stripe for online payments.',
    route:       '/pro/booking-config',
    label:       'Open Configuration',
  },
  {
    icon:        <PeopleAltIcon sx={{ fontSize: 36 }} />,
    colour:      '#0891b2',
    title:       'Stylist & Services',
    description: 'Choose which stylists are bookable online and which services each stylist offers to clients.',
    route:       '/pro/booking-settings/stylists',
    label:       'Open Stylist Settings',
  },
];

export default function ProBookingLanding() {
  const navigate = useNavigate();

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 860, mx: 'auto' }}>
      <Typography variant="h5" sx={{ mb: 0.5, fontWeight: 700 }}>
        Online Booking
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Manage your public booking page and control which stylists and services
        clients can book online.
      </Typography>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 3,
        }}
      >
        {cards.map((card) => (
          <Paper
            key={card.route}
            sx={{
              p: 3,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              transition: 'box-shadow 0.2s',
              '&:hover': { boxShadow: 4 },
            }}
          >
            {/* Icon circle */}
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: '50%',
                background: card.colour,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#fff',
                mb: 0.5,
              }}
            >
              {card.icon}
            </Box>

            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                {card.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {card.description}
              </Typography>
            </Box>

            <Button
              variant="contained"
              endIcon={<ArrowForwardIcon />}
              onClick={() => navigate(card.route)}
              sx={{
                background: card.colour,
                '&:hover': { background: card.colour, filter: 'brightness(0.9)' },
                alignSelf: 'stretch',
              }}
            >
              {card.label}
            </Button>
          </Paper>
        ))}
      </Box>
    </Box>
  );
}